import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAllProviderUsersByProviderId,
  useGetAllSponsorUserList,
  useGetParentsponsorDetails,
  useGetProviderOrgDetails,
  useUpdateVqaVendorContacts,
  useUpdateVqaSponsorContacts,
  useResendVqaContactInvites,
  useAddProviderContact,
  useAddSponsorUser,
} from "../custom-hooks/queries";
import {
  createOverviewSponsorUser,
  OVERVIEW_SPONSOR_MANAGER_PROFILE,
  refreshOverviewOrgUsers,
  resolveOverviewSponsorName,
} from "./overviewContactsShared";
import {
  getVqaOverviewContactPermissions,
  mapVqaOrgUsersToPickerContacts,
  resolveSavedVqaSponsorContacts,
  resolveSavedVqaVendorContacts,
  resolveVqaOverviewEntityId,
  resolveVqaOverviewStatus,
  resolveVqaProviderId,
  resolveVqaProviderName,
  resolveVqaSponsorId,
  resolveVqaVendorHyperComplyOrgId,
  VQA_MANAGER_CREATE_ROLES,
  toRequestContactPayload,
} from "./vqaOverviewContacts";
import { v4 as uuidv4 } from "uuid";

/**
 * Assessment Overview contact managers wiring (vendor + sponsor) for story 14580.
 */
export function useVqaOverviewContacts({
  userRole,
  assessmentDetails,
  refetch,
  activityRefetch,
  sponsorId: preferredSponsorId = null,
}) {
  const { status, isArchived } = useMemo(
    () => resolveVqaOverviewStatus(assessmentDetails),
    [assessmentDetails],
  );

  const permissions = useMemo(
    () => getVqaOverviewContactPermissions(userRole, status, isArchived),
    [userRole, status, isArchived],
  );

  const entityId = useMemo(
    () => resolveVqaOverviewEntityId(assessmentDetails),
    [assessmentDetails],
  );

  const sponsorId = useMemo(
    () => resolveVqaSponsorId(assessmentDetails, preferredSponsorId),
    [assessmentDetails, preferredSponsorId],
  );

  const savedVendorContacts = useMemo(
    () => resolveSavedVqaVendorContacts(assessmentDetails),
    [assessmentDetails],
  );
  const savedSponsorContacts = useMemo(
    () => resolveSavedVqaSponsorContacts(assessmentDetails, sponsorId),
    [assessmentDetails, sponsorId],
  );

  const providerId = resolveVqaProviderId(assessmentDetails);

  const loadVendorUsers =
    Boolean(providerId) &&
    (permissions.canEditVendor || permissions.showVendorPlatformRoles);

  // The assessment payload often omits HyperComply org id; load the provider record whenever
  // we know the vendor id so Create User can send a valid organization_id.
  const {
    data: providerOrgDetails,
    isFetching: providerOrgFetching,
    isFetched: providerOrgFetched,
    isError: providerOrgError,
  } = useGetProviderOrgDetails(providerId || null);
  const vendorHyperComplyOrgId = useMemo(
    () =>
      resolveVqaVendorHyperComplyOrgId({
        providerOrgDetails,
        assessmentDetails,
      }),
    [providerOrgDetails, assessmentDetails],
  );
  const vendorCreateUserBlockedReason = useMemo(() => {
    if (!permissions.canEditVendor) return null;
    if (!providerId) {
      return "Could not resolve this vendor. Refresh the page and try again.";
    }
    if (providerOrgFetching || (!providerOrgFetched && !providerOrgError)) {
      return "Loading vendor organization…";
    }
    if (vendorHyperComplyOrgId) return null;
    if (providerOrgError) {
      return "Could not load vendor organization details. Please try again.";
    }
    return "This vendor has no HyperComply organization yet. Set it up on the vendor profile before creating users.";
  }, [
    permissions.canEditVendor,
    providerId,
    providerOrgFetching,
    providerOrgFetched,
    providerOrgError,
    vendorHyperComplyOrgId,
  ]);
  const loadSponsorUsers =
    Boolean(sponsorId) &&
    (permissions.canEditSponsor || permissions.showSponsorPlatformRoles);

  const { data: vendorUsersData } = useGetAllProviderUsersByProviderId(
    loadVendorUsers ? providerId : null,
    100,
    1,
    "",
  );
  const { data: sponsorUsersData } = useGetAllSponsorUserList({
    currentPage: 1,
    id: loadSponsorUsers ? sponsorId : null,
    search_text: "",
    page_size: 100,
  });
  const { data: sponsorDetails } = useGetParentsponsorDetails(
    loadSponsorUsers ? sponsorId : null,
  );

  const vendorPickerContacts = useMemo(
    () =>
      mapVqaOrgUsersToPickerContacts(vendorUsersData?.items ?? [], {
        mode: "vendor",
      }),
    [vendorUsersData],
  );
  const sponsorPickerContacts = useMemo(
    () =>
      mapVqaOrgUsersToPickerContacts(sponsorUsersData?.items ?? [], {
        mode: "sponsor",
      }),
    [sponsorUsersData],
  );

  const queryClient = useQueryClient();
  const updateVendorContactsMutation = useUpdateVqaVendorContacts();
  const updateSponsorContactsMutation = useUpdateVqaSponsorContacts();
  const resendContactInvitesMutation = useResendVqaContactInvites();
  const { mutateAsync: addProviderContact } = useAddProviderContact();
  const { mutateAsync: addSponsorUser } = useAddSponsorUser();

  const refreshAfterChange = useCallback(async () => {
    await refetch?.();
    await activityRefetch?.();
  }, [refetch, activityRefetch]);

  const saveContacts = useCallback(
    async (mode, draft) => {
      const contacts = toRequestContactPayload(draft);
      if (mode === "sponsor") {
        await updateSponsorContactsMutation.mutateAsync({
          id: entityId,
          contacts,
          sponsor_id: sponsorId,
        });
      } else {
        await updateVendorContactsMutation.mutateAsync({
          id: entityId,
          contacts,
        });
      }
      await refreshAfterChange();
    },
    [
      entityId,
      sponsorId,
      updateSponsorContactsMutation,
      updateVendorContactsMutation,
      refreshAfterChange,
    ],
  );

  const resendInvites = useCallback(
    async (mode, targets, { resendAll = false } = {}) => {
      const emails = resendAll
        ? []
        : (targets || []).map((t) => t.email).filter(Boolean);
      const result = await resendContactInvitesMutation.mutateAsync({
        id: entityId,
        contact_type: mode,
        emails,
        sponsor_id: mode === "sponsor" ? sponsorId : undefined,
      });
      await activityRefetch?.();
      return result;
    },
    [entityId, sponsorId, resendContactInvitesMutation, activityRefetch],
  );

  const createUser = useCallback(
    async (mode, form) => {
      if (mode === "sponsor") {
        await createOverviewSponsorUser(addSponsorUser, {
          form,
          sponsorId,
          sponsorName: resolveOverviewSponsorName(
            assessmentDetails,
            sponsorId,
            sponsorDetails,
            "vqa",
          ),
          hypercomplyOrgId: sponsorDetails?.hypercomply_org_id,
          profile: OVERVIEW_SPONSOR_MANAGER_PROFILE.vqa,
        });
      } else {
        if (!vendorHyperComplyOrgId) {
          throw new Error(
            vendorCreateUserBlockedReason ||
              "Could not resolve organization for this vendor. Please try again in a moment.",
          );
        }
        await addProviderContact({
          provider_user_id: uuidv4(),
          provider_id: providerId,
          organization_id: vendorHyperComplyOrgId,
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
          job_title: form.job_title,
          is_primary: false,
          provider_name: resolveVqaProviderName(assessmentDetails),
          ...VQA_MANAGER_CREATE_ROLES.vendor,
        });
      }

      await refreshOverviewOrgUsers(
        queryClient,
        mode,
        mode === "sponsor" ? sponsorId : providerId,
      );
    },
    [
      addSponsorUser,
      addProviderContact,
      sponsorId,
      providerId,
      vendorHyperComplyOrgId,
      vendorCreateUserBlockedReason,
      assessmentDetails,
      sponsorDetails,
      queryClient,
    ],
  );

  return {
    permissions,
    savedVendorContacts,
    savedSponsorContacts,
    vendorPickerContacts,
    sponsorPickerContacts,
    vendorKnownEmails: (vendorUsersData?.items ?? []).map((u) => u.email),
    sponsorKnownEmails: (sponsorUsersData?.items ?? []).map((u) => u.email),
    savingVendor: updateVendorContactsMutation.isPending,
    savingSponsor: updateSponsorContactsMutation.isPending,
    saveContacts,
    resendInvites,
    createUser,
    vendorCreateUserBlockedReason,
    entityId,
    sponsorId,
  };
}
